import {FC, Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  listCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  saveSteps,
  importContacts,
  getContacts,
  addContact,
  deleteContact,
  stopContact,
  sendToContact,
  releaseContacts,
  getSchedule,
  addTestPhone,
  removeTestPhone,
  sendTest,
  runNow,
  getMessages,
  getClicks,
  getStats,
  getOptOuts,
  removeOptOut,
  OptOutRow,
  SmsCampaign,
  SmsStep,
  SmsLink,
  SmsContact,
  ScheduleData,
  ClickRow,
  StatRow,
  MessageRow,
} from './_requests'

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

const err = (e: any, fallback = 'שגיאה') => e?.response?.data?.error || e?.message || fallback

const fmt = (d?: string | null) => {
  if (!d) return '—'
  const x = new Date(d)
  return isNaN(x.getTime())
    ? '—'
    : x.toLocaleString('he-IL', {day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'})
}
const fmtDay = (iso: string) => {
  const x = new Date(iso + 'T00:00:00')
  return x.toLocaleDateString('he-IL', {weekday: 'short', day: '2-digit', month: '2-digit'})
}
// "Today" is whatever the server says in Israel time — the browser's UTC date
// drifts by a day for a few hours every night.


// A tracked link renders to https://fbzipper.com/s/XXXXXXXX — ~31 chars. Count
// that, not the 6-char {link} token, or the segment estimate lies.
const RENDERED_LINK_LEN = 31

// Seed text for a new follow-up — the nudge that goes out to whoever did not
// opt out of the message above it.
const FOLLOWUP_TEXT = 'רוצה שמישהו יצור איתך קשר בנושא? השב/י כן ונחזור אליך. להסרה: {stop}'

// Hebrew forces UCS-2: 70 chars for a single SMS, 67 per part once it splits.
// GSM-7 (latin only) gets 160 / 153. This is what a 900-char Hebrew blast
// actually costs, and the whole reason the editor shows it.
function segments(text: string): {chars: number; segments: number; ucs2: boolean} {
  const rendered = String(text || '')
    .replace(/\{(link|wa|stop)\}/g, 'x'.repeat(RENDERED_LINK_LEN))
    .replace(/\{name\}/g, 'ישראל ישראלי')
    .replace(/\{first_name\}/g, 'ישראל')
  const ucs2 = /[^\x00-\x7F]/.test(rendered)
  const chars = rendered.length
  if (!chars) return {chars: 0, segments: 0, ucs2}
  const single = ucs2 ? 70 : 160
  const multi = ucs2 ? 67 : 153
  return {chars, segments: chars <= single ? 1 : Math.ceil(chars / multi), ucs2}
}

// Paste box / CSV parsing. One contact per line; phone is the first field that
// looks like a number, anything else on the line becomes the name.
function parseRows(raw: string): {phone: string; name?: string}[] {
  const out: {phone: string; name?: string}[] = []
  String(raw || '')
    .split(/\r?\n/)
    .forEach((line) => {
      const trimmed = line.trim()
      if (!trimmed) return
      const parts = trimmed.split(/[,;\t]/).map((p) => p.trim()).filter(Boolean)
      if (!parts.length) return
      const phoneIdx = parts.findIndex((p) => (p.replace(/\D/g, '') || '').length >= 7)
      if (phoneIdx === -1) return
      const phone = parts[phoneIdx]
      const name = parts.filter((_, i) => i !== phoneIdx).join(' ').trim()
      out.push({phone, name})
    })
  return out
}

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e6e9f0',
  padding: 18,
  marginBottom: 16,
}
const chip = (bg: string, color = '#fff'): React.CSSProperties => ({
  background: bg,
  color,
  borderRadius: 999,
  padding: '2px 10px',
  fontSize: 12,
  fontWeight: 700,
  display: 'inline-block',
})

const STATUS_LABEL: Record<string, string> = {
  pending: 'ממתין',
  active: 'פעיל',
  done: 'סיים',
  stopped: 'הוסר',
  invalid: 'לא תקין',
}
const STATUS_COLOR: Record<string, string> = {
  pending: '#8a94a6',
  active: '#0b8a3d',
  done: '#1877f2',
  stopped: '#c0392b',
  invalid: '#e67e22',
}
const BLOCKED_LABEL: Record<string, string> = {
  campaign_paused: 'הקמפיין מושהה',
  shabbat: 'שבת — לא נשלח',
  outside_send_window: 'מחוץ לשעות השליחה',
  no_steps: 'לא הוגדרו הודעות',
  daily_cap: 'הגעת לתקרה היומית',
}

type Tab = 'steps' | 'contacts' | 'schedule' | 'clicks' | 'tests' | 'history' | 'optouts'

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------

const SmsCampaignsPage: FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [smsConfigured, setSmsConfigured] = useState(true)
  const [linkBase, setLinkBase] = useState('')
  const [campaigns, setCampaigns] = useState<SmsCampaign[]>([])
  const [selectedId, setSelectedId] = useState<string>('')
  const [tab, setTab] = useState<Tab>('steps')

  const selected = useMemo(() => campaigns.find((c) => c.id === selectedId) || null, [campaigns, selectedId])

  const load = useCallback(async (keepId?: string) => {
    setLoading(true)
    setError('')
    try {
      const {data} = await listCampaigns()
      setCampaigns(data.campaigns || [])
      setSmsConfigured(data.sms_configured !== false)
      setLinkBase(data.link_base || '')
      setSelectedId((prev) => {
        const want = keepId || prev
        if (want && (data.campaigns || []).some((c) => c.id === want)) return want
        return (data.campaigns || [])[0]?.id || ''
      })
    } catch (e: any) {
      setError(err(e, 'שגיאה בטעינת הקמפיינים'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const patchCampaign = (c: SmsCampaign) => setCampaigns((prev) => prev.map((x) => (x.id === c.id ? c : x)))

  const onNew = async () => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('שם הקבוצה החדשה:')
    if (!name || !name.trim()) return
    try {
      const {data} = await createCampaign({name: name.trim()})
      await load(data.campaign.id)
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      window.alert(err(e))
    }
  }

  const onDelete = async (c: SmsCampaign) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`למחוק את "${c.name}" על כל אנשי הקשר וההיסטוריה שלה?`)) return
    try {
      await deleteCampaign(c.id)
      await load()
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      window.alert(err(e))
    }
  }

  const onToggleActive = async (c: SmsCampaign) => {
    try {
      const {data} = await updateCampaign(c.id, {active: !c.active} as any)
      patchCampaign(data.campaign)
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      window.alert(err(e))
    }
  }

  return (
    <div style={{padding: 20}} dir='rtl'>
      <div className='d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2'>
        <div>
          <h1 style={{margin: 0, fontSize: 26, fontWeight: 800}}>קמפיין SMS</h1>
          <div style={{opacity: 0.6, fontSize: 14}}>
            רצף הודעות יומי לפי קבוצות — כל איש קשר מקבל את ההודעות לפי היום שבו שוחרר
          </div>
        </div>
        <button className='btn btn-primary' onClick={onNew}>
          <i className='bi bi-plus-lg me-1'></i> קבוצה חדשה
        </button>
      </div>

      {!smsConfigured && (
        <div className='alert alert-warning'>
          <i className='bi bi-exclamation-triangle me-2'></i>
          שירות ה-SMS אינו מוגדר (MULTISEND_USER / MULTISEND_PASSWORD / MULTISEND_FROM) — לא ניתן לשלוח.
        </div>
      )}
      {error && <div className='alert alert-danger'>{error}</div>}

      {loading ? (
        <div className='text-center p-5'>
          <span className='spinner-border' />
        </div>
      ) : (
        <div className='row g-3'>
          <div className='col-lg-3'>
            {campaigns.map((c) => (
              <div
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                style={{
                  ...card,
                  marginBottom: 12,
                  cursor: 'pointer',
                  borderColor: c.id === selectedId ? '#1877f2' : '#e6e9f0',
                  borderWidth: c.id === selectedId ? 2 : 1,
                  padding: 14,
                }}
              >
                <div className='d-flex justify-content-between align-items-start'>
                  <div style={{fontWeight: 800, fontSize: 15}}>{c.name}</div>
                  <span style={chip(c.active ? '#0b8a3d' : '#8a94a6')}>{c.active ? 'פעיל' : 'מושהה'}</span>
                </div>
                {c.description && <div style={{fontSize: 12, opacity: 0.6, marginTop: 2}}>{c.description}</div>}
                <div style={{fontSize: 12, marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap'}}>
                  <span>ממתינים: <b>{c.counts.pending}</b></span>
                  <span>פעילים: <b>{c.counts.active}</b></span>
                  <span>סיימו: <b>{c.counts.done}</b></span>
                </div>
                <div style={{fontSize: 12, opacity: 0.6, marginTop: 4}}>{c.steps.length} הודעות ברצף</div>
              </div>
            ))}
            {!campaigns.length && <div style={{...card, opacity: 0.6}}>אין קבוצות עדיין</div>}
          </div>

          <div className='col-lg-9'>
            {!selected ? (
              <div style={card}>בחר קבוצה מהרשימה</div>
            ) : (
              <Fragment>
                <div style={{...card, paddingBottom: 0}}>
                  <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3'>
                    <div style={{fontSize: 20, fontWeight: 800}}>{selected.name}</div>
                    <div className='d-flex gap-2'>
                      <button
                        className={`btn btn-sm ${selected.active ? 'btn-outline-secondary' : 'btn-success'}`}
                        onClick={() => onToggleActive(selected)}
                      >
                        <i className={`bi ${selected.active ? 'bi-pause-fill' : 'bi-play-fill'} me-1`}></i>
                        {selected.active ? 'השהה' : 'הפעל'}
                      </button>
                      <button className='btn btn-sm btn-outline-danger' onClick={() => onDelete(selected)}>
                        <i className='bi bi-trash'></i>
                      </button>
                    </div>
                  </div>
                  <ul className='nav nav-tabs' style={{borderBottom: 'none'}}>
                    {(
                      [
                        ['steps', 'הודעות', 'bi-chat-square-text'],
                        ['contacts', 'אנשי קשר', 'bi-people'],
                        ['schedule', 'לוח זמנים', 'bi-calendar3'],
                        ['clicks', 'קליקים', 'bi-cursor'],
                        ['tests', 'בדיקות', 'bi-send-check'],
                        ['history', 'היסטוריה', 'bi-clock-history'],
                        ['optouts', 'הסרות', 'bi-slash-circle'],
                      ] as [Tab, string, string][]
                    ).map(([k, label, icon]) => (
                      <li className='nav-item' key={k}>
                        <button
                          className={`nav-link ${tab === k ? 'active' : ''}`}
                          onClick={() => setTab(k)}
                          style={{border: 'none', borderBottom: tab === k ? '3px solid #1877f2' : '3px solid transparent', fontWeight: 700}}
                        >
                          <i className={`bi ${icon} me-1`}></i>
                          {label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {tab === 'steps' && <StepsTab campaign={selected} linkBase={linkBase} onSaved={patchCampaign} />}
                {tab === 'contacts' && <ContactsTab campaign={selected} onCounts={() => load(selected.id)} />}
                {tab === 'schedule' && <ScheduleTab campaign={selected} onRan={() => load(selected.id)} />}
                {tab === 'clicks' && <ClicksTab campaign={selected} />}
                {tab === 'tests' && <TestsTab campaign={selected} onSaved={patchCampaign} />}
                {tab === 'history' && <HistoryTab campaign={selected} />}
                {tab === 'optouts' && <OptOutsTab />}
              </Fragment>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: messages (edit / add / reorder) + tracked links
// ---------------------------------------------------------------------------

const StepsTab: FC<{campaign: SmsCampaign; linkBase: string; onSaved: (c: SmsCampaign) => void}> = ({
  campaign,
  linkBase,
  onSaved,
}) => {
  const [steps, setSteps] = useState<SmsStep[]>(campaign.steps)
  const [links, setLinks] = useState<SmsLink[]>(campaign.links)
  const [sender, setSender] = useState(campaign.sender || '')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const areas = useRef<Record<number, HTMLTextAreaElement | null>>({})

  useEffect(() => {
    setSteps(campaign.steps)
    setLinks(campaign.links)
    setSender(campaign.sender || '')
  }, [campaign.id, campaign.steps, campaign.links, campaign.sender])

  const patch = (i: number, k: keyof SmsStep, v: any) =>
    setSteps((prev) => prev.map((s, idx) => (idx === i ? {...s, [k]: v} : s)))

  const move = (i: number, dir: -1 | 1) =>
    setSteps((prev) => {
      const n = [...prev]
      const j = i + dir
      if (j < 0 || j >= n.length) return prev
      ;[n[i], n[j]] = [n[j], n[i]]
      return n
    })

  const addStep = () => {
    const lastOffset = steps.length ? Math.max(...steps.map((s) => s.day_offset)) : -1
    // key '' marks it as new — the server mints a stable key on save.
    setSteps((prev) => [...prev, {key: '', label: '', day_offset: lastOffset + 1, text: '', delay_minutes: 0}])
  }

  // Every message can have its own follow-up: inserted directly BELOW that
  // message, chasing it after N minutes and reaching only the people who did not
  // opt out in between. It carries its predecessor's day_offset so it stays glued
  // to it (the server re-derives that from the order on save anyway).
  const addFollowupAfter = (i: number) =>
    setSteps((prev) => {
      const n = [...prev]
      n.splice(i + 1, 0, {
        key: '',
        label: `המשך ל${prev[i].label || `הודעה ${i + 1}`}`,
        day_offset: prev[i].day_offset,
        delay_minutes: 5,
        text: FOLLOWUP_TEXT,
      })
      return n
    })

  // Insert a token at the cursor so admins don't have to type braces.
  const insertToken = (i: number, token: string) => {
    const el = areas.current[i]
    const cur = steps[i]?.text || ''
    if (!el) return patch(i, 'text', cur + token)
    const start = el.selectionStart ?? cur.length
    const end = el.selectionEnd ?? cur.length
    patch(i, 'text', cur.slice(0, start) + token + cur.slice(end))
    setTimeout(() => {
      el.focus()
      el.selectionStart = el.selectionEnd = start + token.length
    }, 0)
  }

  const save = async () => {
    setSaving(true)
    setMsg('')
    try {
      await updateCampaign(campaign.id, {links, sender: sender.trim()} as any)
      const {data} = await saveSteps(campaign.id, steps)
      onSaved(data.campaign)
      setSteps(data.campaign.steps)
      setMsg('נשמר ✓')
    } catch (e: any) {
      setMsg(err(e))
    } finally {
      setSaving(false)
    }
  }

  const audience = campaign.counts.active + campaign.counts.pending
  const totalSegments = steps.reduce((a, s) => a + segments(s.text).segments, 0)

  return (
    <Fragment>
      <div style={card}>
        <div style={{fontWeight: 800, marginBottom: 6}}>שם השולח</div>
        <div style={{fontSize: 13, opacity: 0.7, marginBottom: 8}}>
          מה שמופיע לנמען כשולח ההודעה. עד 11 תווים באנגלית/ספרות, או מספר טלפון. ריק = ברירת המחדל הכללית
          {campaign.default_sender ? ` (${campaign.default_sender})` : ''}.
        </div>
        <input
          className='form-control form-control-sm'
          style={{maxWidth: 240}}
          dir='ltr'
          maxLength={15}
          placeholder={campaign.default_sender || 'FBZipper'}
          value={sender}
          onChange={(e) => setSender(e.target.value)}
        />
      </div>

      <div style={card}>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <div style={{fontWeight: 800}}>קישורים נמדדים</div>
          <button className='btn btn-sm btn-outline-primary' onClick={() => setLinks((p) => [...p, {token: '', label: '', url: ''}])}>
            <i className='bi bi-plus-lg'></i> קישור
          </button>
        </div>
        <div style={{fontSize: 13, opacity: 0.7, marginBottom: 10}}>
          כל קישור מקבל כתובת קצרה ייחודית לכל נמען ({linkBase || 'https://fbzipper.com'}/s/XXXXXXXX), כך שאפשר לראות
          בדיוק מי לחץ. השתמשו בטוקן בתוך ההודעה, למשל <code>{'{link}'}</code>.
        </div>
        {links.map((l, i) => (
          <div className='row g-2 mb-2' key={i}>
            <div className='col-md-2'>
              <input
                className='form-control form-control-sm'
                placeholder='טוקן'
                value={l.token}
                onChange={(e) => setLinks((p) => p.map((x, idx) => (idx === i ? {...x, token: e.target.value} : x)))}
              />
            </div>
            <div className='col-md-3'>
              <input
                className='form-control form-control-sm'
                placeholder='תיאור'
                value={l.label}
                onChange={(e) => setLinks((p) => p.map((x, idx) => (idx === i ? {...x, label: e.target.value} : x)))}
              />
            </div>
            <div className='col-md-6'>
              <input
                className='form-control form-control-sm'
                placeholder='https://...'
                dir='ltr'
                value={l.url}
                onChange={(e) => setLinks((p) => p.map((x, idx) => (idx === i ? {...x, url: e.target.value} : x)))}
              />
            </div>
            <div className='col-md-1'>
              <button className='btn btn-sm btn-outline-danger w-100' onClick={() => setLinks((p) => p.filter((_, idx) => idx !== i))}>
                <i className='bi bi-x-lg'></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {steps.map((s, i) => {
        const seg = segments(s.text)
        const noStop = s.text && !s.text.includes('{stop}')
        // Ordinal of the message this card is (or belongs to), ignoring follow-ups.
        const msgNo = steps.slice(0, i + 1).filter((x) => !x.delay_minutes).length
        // A token with no URL behind it is sent verbatim — catch it here rather
        // than in a customer's inbox.
        const dangling = (s.text.match(/\{([a-zA-Z0-9_]+)\}/g) || [])
          .map((t) => t.slice(1, -1))
          .filter((t) => t !== 'name' && t !== 'first_name' && t !== 'stop')
          .filter((t) => !links.some((l) => l.token === t && l.url.trim()))
        return (
          <div style={card} key={s.key || `new-${i}`}>
            <div className='d-flex align-items-center gap-2 mb-2 flex-wrap'>
              <span style={chip(s.delay_minutes > 0 ? '#8e44ad' : '#1877f2')}>
                {/* Follow-ups do not consume a number of their own — they belong to the message above. */}
                {s.delay_minutes > 0 ? `המשך להודעה ${msgNo}` : `הודעה ${msgNo}`}
              </span>
              {s.delay_minutes > 0 ? (
                <div className='input-group input-group-sm' style={{width: 260}}>
                  <span className='input-group-text'>דקות אחרי ההודעה הקודמת</span>
                  <input
                    type='number'
                    min={1}
                    max={1440}
                    className='form-control'
                    value={s.delay_minutes}
                    onChange={(e) => patch(i, 'delay_minutes', Math.min(1440, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                  />
                </div>
              ) : (
                <div className='input-group input-group-sm' style={{width: 150}}>
                  <span className='input-group-text'>יום</span>
                  <input
                    type='number'
                    min={0}
                    className='form-control'
                    value={s.day_offset}
                    onChange={(e) => patch(i, 'day_offset', Math.max(0, parseInt(e.target.value, 10) || 0))}
                  />
                </div>
              )}
              <input
                className='form-control form-control-sm'
                style={{maxWidth: 220}}
                placeholder='כותרת פנימית'
                value={s.label}
                onChange={(e) => patch(i, 'label', e.target.value)}
              />
              <div className='ms-auto d-flex gap-1'>
                {!s.delay_minutes && !(steps[i + 1]?.delay_minutes > 0) && (
                  <button
                    className='btn btn-sm btn-outline-secondary'
                    title='הוסף הודעת המשך להודעה זו — נשלחת דקות אחריה, רק למי שלא ביקש הסרה'
                    onClick={() => addFollowupAfter(i)}
                  >
                    <i className='bi bi-lightning me-1'></i> המשך
                  </button>
                )}
                <button className='btn btn-sm btn-outline-secondary' onClick={() => move(i, -1)} disabled={i === 0}>
                  <i className='bi bi-arrow-up'></i>
                </button>
                <button className='btn btn-sm btn-outline-secondary' onClick={() => move(i, 1)} disabled={i === steps.length - 1}>
                  <i className='bi bi-arrow-down'></i>
                </button>
                <button className='btn btn-sm btn-outline-danger' onClick={() => setSteps((p) => p.filter((_, idx) => idx !== i))}>
                  <i className='bi bi-trash'></i>
                </button>
              </div>
            </div>

            <div className='d-flex gap-1 mb-2 flex-wrap'>
              {['{name}', ...links.filter((l) => l.token).map((l) => `{${l.token}}`), '{stop}'].map((t) => (
                <button key={t} className='btn btn-sm btn-light' style={{fontSize: 12}} onClick={() => insertToken(i, t)}>
                  {t}
                </button>
              ))}
            </div>

            <textarea
              ref={(el) => {
                areas.current[i] = el
              }}
              className='form-control'
              rows={7}
              value={s.text}
              onChange={(e) => patch(i, 'text', e.target.value)}
              placeholder='תוכן ההודעה...'
            />

            <div className='d-flex gap-3 flex-wrap mt-2' style={{fontSize: 13}}>
              <span>
                {seg.chars} תווים · <b>{seg.segments}</b> מקטעי SMS {seg.ucs2 ? '(עברית — 67 תווים למקטע)' : '(לטינית — 153)'}
              </span>
              {audience > 0 && (
                <span style={{opacity: 0.75}}>
                  ≈ <b>{(seg.segments * audience).toLocaleString('he-IL')}</b> מקטעים ל-{audience.toLocaleString('he-IL')} נמענים
                </span>
              )}
              {s.delay_minutes > 0 && (
                <span style={{opacity: 0.75}}>
                  נשלח {s.delay_minutes} דקות אחרי "{steps[i - 1]?.label || `הודעה ${i}`}" — ורק למי שלא ביקש להסיר את עצמו עד אז
                </span>
              )}
              {s.delay_minutes > 0 && i === 0 && <span style={chip('#c0392b')}>הודעת המשך חייבת לבוא אחרי הודעה אחרת</span>}
              {seg.segments > 4 && <span style={chip('#e67e22')}>הודעה ארוכה — {seg.segments} מקטעים לכל נמען</span>}
              {noStop && <span style={chip('#c0392b')}>אין קישור הסרה {'{stop}'}</span>}
              {dangling.length > 0 && (
                <span style={chip('#c0392b')}>טוקן ללא כתובת: {Array.from(new Set(dangling)).map((t) => `{${t}}`).join(' ')}</span>
              )}
            </div>
          </div>
        )
      })}

      <div className='d-flex gap-2 align-items-center flex-wrap'>
        <button className='btn btn-outline-primary' onClick={addStep}>
          <i className='bi bi-plus-lg me-1'></i> הוסף הודעה
        </button>
        <button className='btn btn-primary' onClick={save} disabled={saving}>
          {saving ? 'שומר...' : 'שמור רצף'}
        </button>
        {msg && <span style={{fontWeight: 700}}>{msg}</span>}
        <span className='ms-auto' style={{fontSize: 13, opacity: 0.7}}>
          סה"כ ברצף: <b>{totalSegments}</b> מקטעים לכל איש קשר
        </span>
      </div>
    </Fragment>
  )
}

// ---------------------------------------------------------------------------
// Tab: contacts (import / release / manage)
// ---------------------------------------------------------------------------

const ContactsTab: FC<{campaign: SmsCampaign; onCounts: () => void}> = ({campaign, onCounts}) => {
  const [raw, setRaw] = useState('')
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<string>('')
  const [rows, setRows] = useState<SmsContact[]>([])
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [releaseCount, setReleaseCount] = useState(50)
  const [releasing, setReleasing] = useState(false)
  // Which message the per-row "שלח עכשיו" button fires. '' = next unsent step.
  const [manualStep, setManualStep] = useState('')
  const [sendingId, setSendingId] = useState('')
  const [sendMsg, setSendMsg] = useState<{text: string; ok: boolean} | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const {data} = await getContacts(campaign.id, {status, search, page, limit: 100})
      setRows(data.contacts || [])
      setTotal(data.total || 0)
    } catch (e: any) {
      setResult(err(e))
    } finally {
      setLoading(false)
    }
  }, [campaign.id, status, search, page])

  useEffect(() => {
    load()
  }, [load])

  const doImport = async (source: 'paste' | 'csv', text: string) => {
    const parsed = parseRows(text)
    if (!parsed.length) {
      setResult('לא נמצאו מספרים לייבוא')
      return
    }
    setImporting(true)
    setResult('')
    try {
      const {data} = await importContacts(campaign.id, parsed, source)
      setResult(
        `נוספו ${data.added} · כפולים ${data.duplicates} · לא תקינים ${data.invalid}` +
          (data.opted_out ? ` · ביקשו הסרה ${data.opted_out}` : '') +
          (data.invalid_samples?.length ? ` (${data.invalid_samples.slice(0, 3).join(', ')})` : '')
      )
      setRaw('')
      setPage(1)
      await load()
      onCounts()
    } catch (e: any) {
      setResult(err(e))
    } finally {
      setImporting(false)
    }
  }

  const onFile = (f?: File) => {
    if (!f) return
    const reader = new FileReader()
    reader.onload = () => doImport('csv', String(reader.result || ''))
    reader.readAsText(f)
  }

  const doRelease = async () => {
    setReleasing(true)
    try {
      const {data} = await releaseContacts(campaign.id, releaseCount)
      setResult(`שוחררו ${data.released} אנשי קשר — הרצף מתחיל מהיום`)
      await load()
      onCounts()
    } catch (e: any) {
      setResult(err(e))
    } finally {
      setReleasing(false)
    }
  }

  // One-off send to a single contact. Ignores quiet hours / Shabbat / the daily
  // cap server-side, and enrolls the contact if it was still "ממתין".
  const sendOne = async (c: SmsContact) => {
    const stepName = manualStep
      ? campaign.steps.find((s) => s.key === manualStep)?.label || 'ההודעה שנבחרה'
      : 'ההודעה הבאה ברצף'
    // eslint-disable-next-line no-alert
    if (!window.confirm(`לשלוח עכשיו את "${stepName}" אל ${c.phone}?`)) return
    setSendingId(c.id)
    setSendMsg(null)
    try {
      const {data} = await sendToContact(campaign.id, c.id, manualStep || undefined)
      const label = data.step?.label || data.step?.key || ''
      setSendMsg({
        ok: !!data.ok,
        text: data.ok
          ? `נשלח ל-${c.phone}: ${label}` + (data.enrolled ? ' — ואיש הקשר שוחרר לרצף' : '') + (data.done ? ' — סיים את הרצף' : '')
          : `השליחה ל-${c.phone} נכשלה: ${data.error || ''}`,
      })
      await load()
      onCounts()
    } catch (e: any) {
      setSendMsg({ok: false, text: err(e)})
    } finally {
      setSendingId('')
    }
  }

  const addOne = async () => {
    // eslint-disable-next-line no-alert
    const p = window.prompt('מספר טלפון:')
    if (!p) return
    // eslint-disable-next-line no-alert
    const n = window.prompt('שם (אופציונלי):') || ''
    try {
      await addContact(campaign.id, {phone: p, name: n})
      await load()
      onCounts()
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      window.alert(err(e))
    }
  }

  return (
    <Fragment>
      <div style={card}>
        <div style={{fontWeight: 800, marginBottom: 8}}>ייבוא אנשי קשר</div>
        <div style={{fontSize: 13, opacity: 0.7, marginBottom: 8}}>
          שורה לכל איש קשר. אפשר רק מספר, או "מספר, שם" (גם טאב או נקודה-פסיק). מספרים כפולים בקבוצה נדחים אוטומטית.
        </div>
        <textarea
          className='form-control mb-2'
          rows={5}
          dir='ltr'
          placeholder={'0501234567, ישראל ישראלי\n0521112222'}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
        />
        <div className='d-flex gap-2 flex-wrap align-items-center'>
          <button className='btn btn-primary btn-sm' onClick={() => doImport('paste', raw)} disabled={importing || !raw.trim()}>
            {importing ? 'מייבא...' : `ייבא ${parseRows(raw).length || ''} מספרים`}
          </button>
          <label className='btn btn-outline-primary btn-sm mb-0'>
            <i className='bi bi-upload me-1'></i> העלה קובץ CSV / TXT
            <input type='file' accept='.csv,.txt' hidden onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
          <button className='btn btn-outline-secondary btn-sm' onClick={addOne}>
            <i className='bi bi-plus-lg me-1'></i> הוסף מספר בודד
          </button>
          {result && <span style={{fontWeight: 700, fontSize: 13}}>{result}</span>}
        </div>
      </div>

      <div style={card}>
        <div style={{fontWeight: 800, marginBottom: 6}}>שחרור לרצף</div>
        <div style={{fontSize: 13, opacity: 0.7, marginBottom: 10}}>
          אנשי קשר מיובאים במצב "ממתין" ולא מקבלים כלום. שחרור כמות מסוימת מתחיל עבורם את הרצף מהיום — כך שולטים בקצב.
        </div>
        <div className='d-flex gap-2 align-items-center flex-wrap'>
          <span style={chip('#8a94a6')}>ממתינים: {campaign.counts.pending}</span>
          <input
            type='number'
            min={1}
            className='form-control form-control-sm'
            style={{width: 120}}
            value={releaseCount}
            onChange={(e) => setReleaseCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
          />
          <button className='btn btn-success btn-sm' onClick={doRelease} disabled={releasing || !campaign.counts.pending}>
            {releasing ? 'משחרר...' : `שחרר ${releaseCount} אנשי קשר`}
          </button>
        </div>
      </div>

      <div style={card}>
        <div className='d-flex gap-2 mb-3 flex-wrap align-items-center'>
          <select className='form-select form-select-sm' style={{width: 150}} value={status} onChange={(e) => {setStatus(e.target.value); setPage(1)}}>
            <option value='all'>הכל</option>
            <option value='pending'>ממתינים</option>
            <option value='active'>פעילים</option>
            <option value='done'>סיימו</option>
            <option value='stopped'>הוסרו</option>
          </select>
          <input
            className='form-control form-control-sm'
            style={{width: 220}}
            placeholder='חיפוש שם / טלפון'
            value={search}
            onChange={(e) => {setSearch(e.target.value); setPage(1)}}
          />
          <span style={{fontSize: 13, opacity: 0.7}}>סה"כ {total.toLocaleString('he-IL')}</span>
          <div className='ms-auto d-flex gap-2 align-items-center'>
            <span style={{fontSize: 13, opacity: 0.7}}>שליחה ידנית:</span>
            <select className='form-select form-select-sm' style={{width: 210}} value={manualStep} onChange={(e) => setManualStep(e.target.value)}>
              <option value=''>ההודעה הבאה ברצף</option>
              {campaign.steps.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label || s.key} (יום {s.day_offset})
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{fontSize: 12, opacity: 0.65, marginBottom: 10}}>
          כפתור <i className='bi bi-send'></i> שולח מיד לאיש קשר בודד — מתעלם משעות השקט, שבת והמכסה היומית. איש קשר "ממתין" משוחרר לרצף בשליחה.
        </div>
        {sendMsg && (
          <div style={{fontWeight: 700, fontSize: 13, marginBottom: 10, color: sendMsg.ok ? '#0b8a3d' : '#c0392b'}}>{sendMsg.text}</div>
        )}

        {loading ? (
          <div className='text-center p-4'><span className='spinner-border spinner-border-sm' /></div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table className='table table-sm align-middle'>
              <thead>
                <tr>
                  <th>שם</th>
                  <th>טלפון</th>
                  <th>סטטוס</th>
                  <th>שוחרר</th>
                  <th>נשלחו</th>
                  <th>קליקים</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name || '—'}</td>
                    <td dir='ltr'>{c.phone}</td>
                    <td><span style={chip(STATUS_COLOR[c.status] || '#8a94a6')}>{STATUS_LABEL[c.status] || c.status}</span></td>
                    <td>{fmt(c.enrolled_at)}</td>
                    <td>{c.sent_count}</td>
                    <td>{c.click_count ? <b style={{color: '#0b8a3d'}}>{c.click_count}</b> : '—'}</td>
                    <td className='text-end'>
                      {c.status !== 'stopped' && c.status !== 'invalid' && (
                        <button
                          className='btn btn-sm btn-outline-success me-1'
                          title='שלח עכשיו'
                          disabled={sendingId === c.id}
                          onClick={() => sendOne(c)}
                        >
                          {sendingId === c.id ? (
                            <span className='spinner-border spinner-border-sm' />
                          ) : (
                            <i className='bi bi-send'></i>
                          )}
                        </button>
                      )}
                      {c.status !== 'stopped' && (
                        <button
                          className='btn btn-sm btn-outline-warning me-1'
                          title='הסר מהרשימה'
                          onClick={async () => {
                            await stopContact(campaign.id, c.id)
                            load()
                            onCounts()
                          }}
                        >
                          <i className='bi bi-slash-circle'></i>
                        </button>
                      )}
                      <button
                        className='btn btn-sm btn-outline-danger'
                        onClick={async () => {
                          await deleteContact(campaign.id, c.id)
                          load()
                          onCounts()
                        }}
                      >
                        <i className='bi bi-trash'></i>
                      </button>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={7} className='text-center' style={{opacity: 0.6}}>אין אנשי קשר</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {total > 100 && (
          <div className='d-flex gap-2 justify-content-center'>
            <button className='btn btn-sm btn-outline-secondary' disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              הקודם
            </button>
            <span style={{fontSize: 13, alignSelf: 'center'}}>עמוד {page} מתוך {Math.ceil(total / 100)}</span>
            <button className='btn btn-sm btn-outline-secondary' disabled={page >= Math.ceil(total / 100)} onClick={() => setPage((p) => p + 1)}>
              הבא
            </button>
          </div>
        )}
      </div>
    </Fragment>
  )
}

// ---------------------------------------------------------------------------
// Tab: the daily flow
// ---------------------------------------------------------------------------

const ScheduleTab: FC<{campaign: SmsCampaign; onRan: () => void}> = ({campaign, onRan}) => {
  const [data, setData] = useState<ScheduleData | null>(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [msg, setMsg] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await getSchedule(campaign.id, 14)
      setData(r.data)
    } catch (e: any) {
      setMsg(err(e))
    } finally {
      setLoading(false)
    }
  }, [campaign.id])

  useEffect(() => {
    load()
  }, [load])

  const doRun = async (force: boolean) => {
    setRunning(true)
    setMsg('')
    try {
      const {data: r} = await runNow(campaign.id, force)
      const res = r.result?.campaigns?.[0]?.result
      setMsg(
        res
          ? `נשלחו ${res.sent} · נכשלו ${res.failed} · דולגו ${res.skipped}${res.blocked ? ` · ${BLOCKED_LABEL[res.blocked] || res.blocked}` : ''}`
          : r.result?.reason || 'הסתיים'
      )
      await load()
      onRan()
    } catch (e: any) {
      setMsg(err(e))
    } finally {
      setRunning(false)
    }
  }

  const max = useMemo(() => Math.max(1, ...(data?.days || []).map((d) => d.total)), [data])

  return (
    <Fragment>
      <div style={card}>
        <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
          <div style={{fontWeight: 800}}>הרצף</div>
          <div className='d-flex gap-2 align-items-center flex-wrap'>
            {data?.blocked_reason && <span style={chip('#e67e22')}>{BLOCKED_LABEL[data.blocked_reason] || data.blocked_reason}</span>}
            <span style={{fontSize: 13, opacity: 0.7}}>תקרה יומית: {data?.daily_cap}</span>
            <button className='btn btn-sm btn-outline-primary' onClick={() => doRun(false)} disabled={running}>
              {running ? 'מריץ...' : 'הפעל עכשיו'}
            </button>
            <button className='btn btn-sm btn-outline-danger' onClick={() => doRun(true)} disabled={running} title='מתעלם משעות השליחה ומשבת'>
              הפעל בכפייה
            </button>
          </div>
        </div>
        {msg && <div className='mt-2' style={{fontWeight: 700, fontSize: 13}}>{msg}</div>}
        <div className='d-flex align-items-center gap-2 mt-3 flex-wrap'>
          {(data?.steps || []).map((s, i) => (
            <Fragment key={s.key}>
              {i > 0 && <i className='bi bi-arrow-left' style={{opacity: 0.4}}></i>}
              <div style={{border: '1px solid #d7dce5', borderRadius: 10, padding: '8px 14px', textAlign: 'center', minWidth: 96}}>
                <div style={{fontSize: 12, opacity: 0.6}}>יום {s.day_offset}</div>
                <div style={{fontWeight: 700, fontSize: 13}}>{s.label || `הודעה ${i + 1}`}</div>
              </div>
            </Fragment>
          ))}
          {!data?.steps?.length && <span style={{opacity: 0.6}}>לא הוגדרו הודעות</span>}
        </div>
      </div>

      <div style={card}>
        <div className='d-flex justify-content-between mb-3'>
          <div style={{fontWeight: 800}}>14 הימים הקרובים</div>
          <span style={{fontSize: 13, opacity: 0.7}}>ממתינים לשחרור: <b>{data?.pending ?? 0}</b></span>
        </div>
        {loading ? (
          <div className='text-center p-4'><span className='spinner-border spinner-border-sm' /></div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table className='table table-sm align-middle'>
              <thead>
                <tr>
                  <th style={{width: 130}}>תאריך</th>
                  <th style={{width: 90}}>סה"כ</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(data?.days || []).map((d) => {
                  const over = !!(data && data.daily_cap && d.total > data.daily_cap)
                  const today = d.date === data?.today
                  return (
                    <tr key={d.date} style={{background: today ? '#f2f7ff' : undefined}}>
                      <td>
                        {fmtDay(d.date)} {today && <span style={chip('#1877f2')}>היום</span>}
                      </td>
                      <td>
                        <b>{d.total}</b> {over && <span style={chip('#e67e22')}>מעל התקרה</span>}
                      </td>
                      <td>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                          <div
                            style={{
                              height: 10,
                              borderRadius: 999,
                              background: over ? '#e67e22' : '#1877f2',
                              width: `${Math.round((d.total / max) * 100)}%`,
                              minWidth: d.total ? 6 : 0,
                            }}
                          />
                          <div style={{display: 'flex', gap: 4, flexWrap: 'wrap'}}>
                            {d.steps.map((s) => (
                              <span key={s.key} style={{...chip('#eef2f8', '#39445a'), fontWeight: 600}}>
                                {s.label || `יום ${s.day_offset}`}: {s.count}
                              </span>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Fragment>
  )
}

// ---------------------------------------------------------------------------
// Tab: clicks
// ---------------------------------------------------------------------------

const ClicksTab: FC<{campaign: SmsCampaign}> = ({campaign}) => {
  const [clicks, setClicks] = useState<ClickRow[]>([])
  const [stats, setStats] = useState<StatRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const [c, s] = await Promise.all([getClicks(campaign.id), getStats(campaign.id)])
        setClicks(c.data.clicks || [])
        setStats(s.data.steps || [])
      } catch (e: any) {
        setError(err(e))
      } finally {
        setLoading(false)
      }
    })()
  }, [campaign.id])

  if (loading) return <div style={card} className='text-center'><span className='spinner-border spinner-border-sm' /></div>
  if (error) return <div className='alert alert-danger'>{error}</div>

  return (
    <Fragment>
      <div style={card}>
        <div style={{fontWeight: 800, marginBottom: 10}}>ביצועים לפי הודעה</div>
        <div style={{overflowX: 'auto'}}>
          <table className='table table-sm align-middle'>
            <thead>
              <tr>
                <th>הודעה</th>
                <th>יום</th>
                <th>נשלחו</th>
                <th>נכשלו</th>
                <th>לחצו</th>
                <th>סה"כ קליקים</th>
                <th>אחוז הקלקה</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.key}>
                  <td>{s.label || s.key}</td>
                  <td>{s.day_offset}</td>
                  <td>{s.sent}</td>
                  <td>{s.failed ? <span style={{color: '#c0392b'}}>{s.failed}</span> : 0}</td>
                  <td><b>{s.clicked}</b></td>
                  <td>{s.total_clicks}</td>
                  <td>{s.ctr ? <b style={{color: '#0b8a3d'}}>{s.ctr}%</b> : '—'}</td>
                </tr>
              ))}
              {!stats.length && <tr><td colSpan={7} className='text-center' style={{opacity: 0.6}}>אין נתונים</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div style={card}>
        <div style={{fontWeight: 800, marginBottom: 10}}>מי לחץ ({clicks.length})</div>
        <div style={{overflowX: 'auto'}}>
          <table className='table table-sm align-middle'>
            <thead>
              <tr>
                <th>שם</th>
                <th>טלפון</th>
                <th>הודעה</th>
                <th>קישור</th>
                <th>קליקים</th>
                <th>לחיצה ראשונה</th>
                <th>אחרונה</th>
              </tr>
            </thead>
            <tbody>
              {clicks.map((c, i) => (
                <tr key={`${c.message_id}-${c.token}-${i}`}>
                  <td>{c.name || '—'} {c.is_test && <span style={chip('#8a94a6')}>בדיקה</span>}</td>
                  <td dir='ltr'>{c.phone}</td>
                  <td>{c.step_label || `יום ${c.day_offset}`}</td>
                  <td>
                    <span style={chip('#eef2f8', '#39445a')}>{`{${c.token}}`}</span>{' '}
                    <a href={c.url} target='_blank' rel='noreferrer' style={{fontSize: 12}} dir='ltr'>
                      {c.url.slice(0, 40)}
                    </a>
                  </td>
                  <td><b>{c.clicks}</b></td>
                  <td>{fmt(c.first_click_at)}</td>
                  <td>{fmt(c.last_click_at)}</td>
                </tr>
              ))}
              {!clicks.length && <tr><td colSpan={7} className='text-center' style={{opacity: 0.6}}>עדיין לא נרשמו קליקים</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Fragment>
  )
}

// ---------------------------------------------------------------------------
// Tab: tests
// ---------------------------------------------------------------------------

const TestsTab: FC<{campaign: SmsCampaign; onSaved: (c: SmsCampaign) => void}> = ({campaign, onSaved}) => {
  const [phones, setPhones] = useState<string[]>(campaign.test_phones)
  const [newPhone, setNewPhone] = useState('')
  const [stepKey, setStepKey] = useState(campaign.steps[0]?.key || '')
  const [sending, setSending] = useState(false)
  const [results, setResults] = useState<{phone: string; ok: boolean; error: string; text: string}[]>([])
  const [msg, setMsg] = useState('')

  useEffect(() => {
    setPhones(campaign.test_phones)
    setStepKey(campaign.steps[0]?.key || '')
  }, [campaign.id, campaign.test_phones, campaign.steps])

  const add = async () => {
    if (!newPhone.trim()) return
    try {
      const {data} = await addTestPhone(campaign.id, newPhone.trim())
      setPhones(data.test_phones)
      setNewPhone('')
      onSaved({...campaign, test_phones: data.test_phones})
    } catch (e: any) {
      setMsg(err(e))
    }
  }

  const remove = async (p: string) => {
    try {
      const {data} = await removeTestPhone(campaign.id, p)
      setPhones(data.test_phones)
      onSaved({...campaign, test_phones: data.test_phones})
    } catch (e: any) {
      setMsg(err(e))
    }
  }

  const send = async (only?: string) => {
    setSending(true)
    setMsg('')
    setResults([])
    try {
      const {data} = await sendTest(campaign.id, {step_key: stepKey, phones: only ? [only] : undefined})
      setResults(data.results || [])
    } catch (e: any) {
      setMsg(err(e))
    } finally {
      setSending(false)
    }
  }

  const step = campaign.steps.find((s) => s.key === stepKey)

  return (
    <Fragment>
      <div style={card}>
        <div style={{fontWeight: 800, marginBottom: 8}}>מספרי בדיקה</div>
        <div style={{fontSize: 13, opacity: 0.7, marginBottom: 10}}>
          המספרים נשמרים לקבוצה. שליחת בדיקה לא נספרת בסטטיסטיקות ולא בתקרה היומית, אבל הקישורים אמיתיים — אפשר ללחוץ
          ולראות שהקליק נרשם.
        </div>
        <div className='d-flex gap-2 mb-3 flex-wrap'>
          <input
            className='form-control form-control-sm'
            style={{width: 200}}
            dir='ltr'
            placeholder='0501234567'
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
          />
          <button className='btn btn-sm btn-outline-primary' onClick={add}>
            <i className='bi bi-plus-lg'></i> הוסף מספר
          </button>
        </div>
        <div className='d-flex gap-2 flex-wrap'>
          {phones.map((p) => (
            <span key={p} style={{...chip('#eef2f8', '#39445a'), padding: '6px 12px', display: 'inline-flex', gap: 8, alignItems: 'center'}}>
              <span dir='ltr'>{p}</span>
              <button className='btn btn-sm p-0' style={{lineHeight: 1}} onClick={() => send(p)} title='שלח למספר הזה'>
                <i className='bi bi-send'></i>
              </button>
              <button className='btn btn-sm p-0' style={{lineHeight: 1, color: '#c0392b'}} onClick={() => remove(p)}>
                <i className='bi bi-x-lg'></i>
              </button>
            </span>
          ))}
          {!phones.length && <span style={{opacity: 0.6, fontSize: 13}}>לא הוגדרו מספרי בדיקה</span>}
        </div>
      </div>

      <div style={card}>
        <div className='d-flex gap-2 align-items-center flex-wrap mb-3'>
          <select className='form-select form-select-sm' style={{width: 260}} value={stepKey} onChange={(e) => setStepKey(e.target.value)}>
            {campaign.steps.map((s, i) => (
              <option key={s.key} value={s.key}>
                יום {s.day_offset} — {s.label || `הודעה ${i + 1}`}
              </option>
            ))}
          </select>
          <button className='btn btn-primary btn-sm' onClick={() => send()} disabled={sending || !phones.length || !stepKey}>
            {sending ? 'שולח...' : `שלח בדיקה לכל ${phones.length} המספרים`}
          </button>
          {msg && <span style={{fontWeight: 700, fontSize: 13}}>{msg}</span>}
        </div>

        {step && (
          <div style={{background: '#f7f9fc', borderRadius: 10, padding: 14, whiteSpace: 'pre-wrap', fontSize: 14}}>
            {step.text || 'ההודעה ריקה'}
          </div>
        )}

        {results.map((r, i) => (
          <div key={i} className='mt-3' style={{borderTop: '1px solid #eef1f6', paddingTop: 10}}>
            <div>
              <span style={chip(r.ok ? '#0b8a3d' : '#c0392b')}>{r.ok ? 'נשלח' : 'נכשל'}</span>{' '}
              <span dir='ltr'>{r.phone}</span> {r.error && <span style={{color: '#c0392b', fontSize: 13}}>{r.error}</span>}
            </div>
            <div style={{background: '#f7f9fc', borderRadius: 10, padding: 12, whiteSpace: 'pre-wrap', fontSize: 13, marginTop: 6}}>
              {r.text}
            </div>
          </div>
        ))}
      </div>
    </Fragment>
  )
}

// ---------------------------------------------------------------------------
// Tab: history
// ---------------------------------------------------------------------------

const HistoryTab: FC<{campaign: SmsCampaign}> = ({campaign}) => {
  const [rows, setRows] = useState<MessageRow[]>([])
  const [loading, setLoading] = useState(true)
  const [includeTests, setIncludeTests] = useState(false)
  const [status, setStatus] = useState('all')

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const {data} = await getMessages(campaign.id, {
          status,
          include_tests: includeTests ? '1' : '0',
          limit: 200,
        })
        setRows(data.messages || [])
      } finally {
        setLoading(false)
      }
    })()
  }, [campaign.id, includeTests, status])

  return (
    <div style={card}>
      <div className='d-flex gap-3 align-items-center mb-3 flex-wrap'>
        <div style={{fontWeight: 800}}>היסטוריית שליחה</div>
        <select className='form-select form-select-sm' style={{width: 140}} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value='all'>הכל</option>
          <option value='sent'>נשלחו</option>
          <option value='failed'>נכשלו</option>
        </select>
        <label className='d-flex align-items-center gap-1 mb-0' style={{fontSize: 13}}>
          <input type='checkbox' checked={includeTests} onChange={(e) => setIncludeTests(e.target.checked)} />
          כולל בדיקות
        </label>
      </div>
      {loading ? (
        <div className='text-center p-4'><span className='spinner-border spinner-border-sm' /></div>
      ) : (
        <div style={{overflowX: 'auto'}}>
          <table className='table table-sm align-middle'>
            <thead>
              <tr>
                <th>מתי</th>
                <th>שם</th>
                <th>טלפון</th>
                <th>הודעה</th>
                <th>סטטוס</th>
                <th>קליקים</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id}>
                  <td style={{whiteSpace: 'nowrap'}}>{fmt(m.created_at)}</td>
                  <td>{m.name || '—'} {m.is_test && <span style={chip('#8a94a6')}>בדיקה</span>}</td>
                  <td dir='ltr'>{m.phone}</td>
                  <td style={{maxWidth: 380, fontSize: 12, opacity: 0.8}} title={m.text}>
                    <b>{m.step_label || `יום ${m.day_offset}`}</b> · {m.text.slice(0, 70)}…
                  </td>
                  <td>
                    <span style={chip(m.status === 'sent' ? '#0b8a3d' : '#c0392b')}>{m.status === 'sent' ? 'נשלח' : 'נכשל'}</span>
                    {m.error && <div style={{fontSize: 11, color: '#c0392b'}}>{m.error}</div>}
                  </td>
                  <td>{m.clicks ? <b style={{color: '#0b8a3d'}}>{m.clicks}</b> : '—'}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className='text-center' style={{opacity: 0.6}}>אין היסטוריה</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: global opt-out list
// ---------------------------------------------------------------------------

const OptOutsTab: FC = () => {
  const [rows, setRows] = useState<OptOutRow[]>([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const {data} = await getOptOuts()
      setRows(data.opt_outs || [])
    } catch (e: any) {
      setMsg(err(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const undo = async (phone: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`להחזיר את ${phone} לתפוצה? עשה זאת רק אם ההסרה נעשתה בטעות.`)) return
    try {
      await removeOptOut(phone)
      await load()
    } catch (e: any) {
      setMsg(err(e))
    }
  }

  return (
    <div style={card}>
      <div style={{fontWeight: 800, marginBottom: 6}}>רשימת הסרה ({rows.length})</div>
      <div style={{fontSize: 13, opacity: 0.7, marginBottom: 12}}>
        הרשימה גלובלית — מספר שביקש הסרה לא יקבל הודעות מאף קבוצה, גם לא אחרי ייבוא חוזר. הסרה נרשמת כשלוחצים על
        קישור ההסרה בהודעה, או כשמסירים איש קשר ידנית.
      </div>
      {msg && <div className='alert alert-danger'>{msg}</div>}
      {loading ? (
        <div className='text-center p-4'><span className='spinner-border spinner-border-sm' /></div>
      ) : (
        <div style={{overflowX: 'auto'}}>
          <table className='table table-sm align-middle'>
            <thead>
              <tr>
                <th>טלפון</th>
                <th>שם</th>
                <th>מקור</th>
                <th>קבוצה</th>
                <th>מתי</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.phone}>
                  <td dir='ltr'>{o.phone}</td>
                  <td>{o.name || '—'}</td>
                  <td>
                    <span style={chip(o.source === 'link' ? '#c0392b' : '#8a94a6')}>
                      {o.source === 'link' ? 'הסרה עצמית' : 'הוסר ידנית'}
                    </span>
                  </td>
                  <td>{o.campaign || '—'}</td>
                  <td>{fmt(o.created_at)}</td>
                  <td className='text-end'>
                    <button className='btn btn-sm btn-outline-secondary' onClick={() => undo(o.phone)} title='בטל הסרה'>
                      <i className='bi bi-arrow-counterclockwise'></i>
                    </button>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={6} className='text-center' style={{opacity: 0.6}}>אף אחד לא ביקש הסרה</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default SmsCampaignsPage
